import structureData from './bracketStructureV2.generated.json'
import { THIRD_PLACE_COMBINATION_ROWS } from './thirdPlaceCombinationDataV2'

const THIRD_PLACE_SLOTS = ['1A', '1B', '1D', '1E', '1G', '1I', '1K', '1L']

const parseSource = (placeholder, counterpartPlaceholder) => {
  const value = String(placeholder)
  const placingMatch = value.match(/^([12])([A-L])$/)
  if (placingMatch) {
    return placingMatch[1] === '1'
      ? { type: 'winner', group: placingMatch[2] }
      : { type: 'runnerUp', group: placingMatch[2] }
  }

  if (/^3[A-L]+$/.test(value)) {
    const slotMatch = String(counterpartPlaceholder).match(/^1([A-L])$/)
    return { type: 'thirdPlaceSlot', slot: slotMatch ? `1${slotMatch[1]}` : null }
  }

  return null
}

export const ROUND_OF_32_MATCHUPS = structureData.roundOf32Matchups.map((matchup) => ({
  matchNumber: matchup.matchNumber,
  pickStart: matchup.pickStart,
  home: parseSource(matchup.placeholderA, matchup.placeholderB),
  away: parseSource(matchup.placeholderB, matchup.placeholderA),
}))

export const parseThirdPlaceCombinationMap = () => {
  return THIRD_PLACE_COMBINATION_ROWS
    .trim()
    .split('\n')
    .reduce((combinationMap, line) => {
      const [, ...values] = line.trim().split(/\s+/)
      const groups = values.map(value => value.replace(/^3/, ''))
      const key = [...groups].sort().join('')

      combinationMap[key] = THIRD_PLACE_SLOTS.reduce((slotAssignments, slot, idx) => {
        slotAssignments[slot] = groups[idx]
        return slotAssignments
      }, {})

      return combinationMap
    }, {})
}

const THIRD_PLACE_COMBINATION_MAP = parseThirdPlaceCombinationMap()

const buildPlacementsByGroup = (groupRankings) => {
  return Object.entries(groupRankings ?? {}).reduce((placementsByGroup, [groupName, placements]) => {
    placementsByGroup[String(groupName).toUpperCase()] = {
      winner: placements?.[0],
      runnerUp: placements?.[1],
      third: placements?.[2],
    }
    return placementsByGroup
  }, {})
}

const buildThirdPlaceGroupMap = (placementsByGroup) => {
  return Object.entries(placementsByGroup).reduce((thirdPlaceGroups, [groupName, placements]) => {
    if (placements.third) {
      thirdPlaceGroups[String(placements.third)] = String(groupName).toUpperCase()
    }
    return thirdPlaceGroups
  }, {})
}

const resolveThirdPlaceSlots = (advancingThirdPlaceGroups) => {
  if (advancingThirdPlaceGroups.length !== THIRD_PLACE_SLOTS.length) return {}
  return THIRD_PLACE_COMBINATION_MAP[[...advancingThirdPlaceGroups].sort().join('')] ?? {}
}

const resolveSourceTeam = (source, placementsByGroup, thirdPlaceSlotAssignments) => {
  if (source.type === 'winner') return placementsByGroup[source.group]?.winner
  if (source.type === 'runnerUp') return placementsByGroup[source.group]?.runnerUp

  const thirdPlaceGroup = thirdPlaceSlotAssignments[source.slot]
  return thirdPlaceGroup ? placementsByGroup[thirdPlaceGroup]?.third : undefined
}

const buildSourceLabel = (source, thirdPlaceSlotAssignments) => {
  if (source.type === 'winner') return `Group ${source.group} Winner`
  if (source.type === 'runnerUp') return `Group ${source.group} Runner-up`

  const thirdPlaceGroup = thirdPlaceSlotAssignments[source.slot]
  return thirdPlaceGroup ? `Group ${thirdPlaceGroup} Third` : 'Advancing Third Place'
}

export const buildKnockoutStageV2 = (groupRankings, advancingThirdPlaceTeams) => {
  const placementsByGroup = buildPlacementsByGroup(groupRankings)
  const thirdPlaceGroupsByTeam = buildThirdPlaceGroupMap(placementsByGroup)
  const advancingThirdPlaceGroups = advancingThirdPlaceTeams
    .map(teamId => thirdPlaceGroupsByTeam[String(teamId)])
    .filter(Boolean)

  const thirdPlaceSlotAssignments = resolveThirdPlaceSlots(advancingThirdPlaceGroups)

  const roundOnePicks = ROUND_OF_32_MATCHUPS.reduce((picks, matchup) => {
    picks[`pick_${matchup.pickStart}`] = resolveSourceTeam(matchup.home, placementsByGroup, thirdPlaceSlotAssignments)
    picks[`pick_${matchup.pickStart + 1}`] = resolveSourceTeam(matchup.away, placementsByGroup, thirdPlaceSlotAssignments)
    return picks
  }, {})

  const roundOneSourceLabels = ROUND_OF_32_MATCHUPS.reduce((sourceLabels, matchup) => {
    sourceLabels[`pick_${matchup.pickStart}`] = buildSourceLabel(matchup.home, thirdPlaceSlotAssignments)
    sourceLabels[`pick_${matchup.pickStart + 1}`] = buildSourceLabel(matchup.away, thirdPlaceSlotAssignments)
    return sourceLabels
  }, {})

  return {
    placementsByGroup,
    roundOnePicks,
    roundOneSourceLabels,
    roundOf32: ROUND_OF_32_MATCHUPS,
    thirdPlaceSlotAssignments,
  }
}
